import { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { 
  ChevronRight, Check, X, Lock, Circle, User,
  ArrowRight, Share2, ArrowLeft, Sparkles, CreditCard, AlertTriangle, Pill, 
  ClipboardList, Users, Activity, HelpCircle, Camera, CheckCircle
} from 'lucide-react'
import { useProfileStore, useUserStore, useCaseStore } from '../../stores'
import AppHeader from '../../components/AppHeader'
import { useI18n } from '../../i18n'
import LanguageSelector from '../../components/LanguageSelector'
import HamburgerMenu from '../../components/HamburgerMenu'
import MedicalDocumentUpload from '../../components/MedicalDocumentUpload'
import OrellaChatAgent from '../../components/agent/OrellaChatAgent'
import OrellaLoader from '../../components/OrellaLoader'
import OrellaPageHeader from '../../components/OrellaPageHeader'
import { useAgent } from '../../components/AgentContext'
import { buildProfileSummary } from '../../utils/profileSummary'
import { toConditionList, mergeImportedConditions, mergeImportedFamilyConditions } from '../../utils/profileConditions'
// UX-1 / TF-02 / TF-03 (v242.61.0): the ONE definition of section completeness.
// This screen used to carry its own, and it disagreed with onboarding's and with
// the identification form's. See src/utils/profileCompletion.js for the four.
import { isSectionComplete, requiredCompletionPercent, optionalCompletionPercent,
         allRequiredComplete as allRequiredCompleteFn } from '../../utils/profileCompletion'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import { formatSex } from '../../utils/formatSex'
import { calculateAge as ageFromDob } from '../../utils/dateUtils'

// Section definitions with icons
const SECTION_DEFS_STRUCTURE = [
  { id: 'identification', icon: CreditCard,     labelKey: 'identificationLabel', subtitleKey: 'identificationSubtitle', path: '/profile/identification', required: true },
  { id: 'insurance',      icon: Lock,            labelKey: 'insuranceLabel',      subtitleKey: 'insuranceSubtitle',      path: '/profile/insurance',      required: true },
  { id: 'allergies',      icon: AlertTriangle,   labelKey: 'allergiesLabel',      subtitleKey: 'allergiesSubtitle',      path: '/profile/allergies',      required: true },
  { id: 'medications',    icon: Pill,            labelKey: 'medicationsLabel',    subtitleKey: 'medicationsSubtitle',    path: '/profile/medications',    required: true },
  { id: 'medical-history',icon: ClipboardList,   labelKey: 'medicalHistoryLabel', subtitleKey: 'medicalHistorySubtitle', path: '/profile/medical-history',required: false },
  { id: 'family-history', icon: Users,           labelKey: 'familyHistoryLabel',  subtitleKey: 'familyHistorySubtitle',  path: '/profile/family-history', required: false },
  { id: 'social-lifestyle',icon: Activity,       labelKey: 'socialLifestyleLabel',subtitleKey: 'socialLifestyleSubtitle',path: '/profile/social-lifestyle',required: false },
]

function getAdaptiveMessage(completedIds, allRequiredComplete, t) {
  const count = completedIds.length
  if (allRequiredComplete) {
    return t('profileMain.allSetMessage')
  }
  const requiredSections = SECTION_DEFS_STRUCTURE.filter(s => s.required)
  const nextRequired = requiredSections.find(s => !completedIds.includes(s.id))
  if (count === 0) return t('profileMain.sectionIntroIdentification')
  const messages = {
    'insurance': t('profileMain.sectionIntroInsurance'),
    'allergies': t('profileMain.sectionIntroAllergies'),
    'medications': t('profileMain.sectionIntroMedications'),
  }
  if (nextRequired && messages[nextRequired.id]) {
    if (count >= 2) {
      const remaining = requiredSections.filter(s => !completedIds.includes(s.id)).length
      const nextLabel = t('profileMain.' + nextRequired.labelKey)
      return remaining === 1
        ? t('profileMain.almostThere', { section: nextLabel.toLowerCase() })
        : messages[nextRequired.id]
    }
    return messages[nextRequired.id]
  }
  return t('profileMain.keepGoing')
}

function getSectionSummary(id, profile) {
  // Safe name extractor for conditions/surgeries that may be objects or strings
  const safeName = (item) => {
    if (!item) return ''
    if (typeof item === 'string') return item
    if (typeof item === 'object') return item.name || item.condition || item.label || ''
    return String(item)
  }

  switch (id) {
    case 'identification': {
      const p = profile?.identification
      const parts = [p?.firstName, p?.lastName].filter(Boolean).join(' ')
      return parts ? `${parts}${p?.dob ? ` \u00B7 ${p.dob}` : ''}${p?.sex ? ` \u00B7 ${formatSex(p.sex)}` : ''}` : null
    }
    case 'insurance': {
      const ins = profile?.insurance
      if (ins?.selfPay) return 'Self-pay'
      const name = ins?.insurerName || ins?.planName || ins?.provider
      const memberId = ins?.memberId
      if (name && memberId) return `${name} \u00B7 ${memberId}`
      return name || 'Insurance added'
    }
    case 'allergies': {
      const a = profile?.allergies
      if (a?.noKnownAllergies) return 'No known allergies (NKDA)'
      const items = a?.list?.map(i => safeName(i)).filter(Boolean) || []
      if (items.length === 0) return 'Allergies recorded'
      const count = items.length
      return `${count} allerg${count === 1 ? 'y' : 'ies'}: ${items.slice(0, 2).join(', ')}${count > 2 ? ` +${count - 2} more` : ''}`
    }
    case 'medications': {
      const m = profile?.medications
      if (m?.noMedications) return 'No current medications'
      const items = m?.list?.map(i => safeName(i)).filter(Boolean) || []
      if (items.length === 0) return 'Medications recorded'
      const count = items.length
      return `${count} medication${count === 1 ? '' : 's'}: ${items.slice(0, 2).join(', ')}${count > 2 ? ` +${count - 2} more` : ''}`
    }
    case 'medical-history': {
      const mh = profile?.medicalHistory
      if (!mh) return 'Medical history recorded'
      const parts = []
      // Count conditions
      const conditions = mh.conditions || []
      const condNames = (Array.isArray(conditions) ? conditions : Object.entries(conditions).filter(([k, v]) => v).map(([k]) => k))
        .map(c => safeName(c)).filter(Boolean)
      if (condNames.length > 0) parts.push(`${condNames.length} condition${condNames.length === 1 ? '' : 's'}`)
      // Count surgeries
      const surgeries = mh.surgeries || []
      if (Array.isArray(surgeries) && surgeries.length > 0) parts.push(`${surgeries.length} surger${surgeries.length === 1 ? 'y' : 'ies'}`)
      if (parts.length === 0) return 'No conditions or surgeries'
      // Show first condition name for context
      const firstCond = condNames[0]
      return firstCond ? `${firstCond}${parts.length > 1 ? ` \u00B7 ${parts[1]}` : ''}` : parts.join(' \u00B7 ')
    }
    case 'family-history': {
      const fh = profile?.familyHistory
      if (!fh) return 'Family history recorded'
      const conditions = fh.conditions || []
      // Conditions are stored as array of { id, nameKey, mother, father, sibling }
      // Only count conditions where at least one family member is checked
      const selected = Array.isArray(conditions)
        ? conditions.filter(c => c.mother || c.father || c.sibling || c.grandparent)
        : Object.entries(conditions).filter(([k, v]) => v && typeof v === 'object' && (v.mother || v.father || v.sibling))
      const other = fh.otherConditions || []
      const otherCount = Array.isArray(other) ? other.filter(c => c.name || c.condition).length : 0
      const totalCount = selected.length + otherCount
      if (totalCount === 0) return 'No family conditions noted'
      // Build descriptive summary
      const names = selected.map(c => {
        const relatives = [c.mother && 'Mother', c.father && 'Father', c.sibling && 'Sibling', c.grandparent && 'Grandparent'].filter(Boolean)
        const name = c.nameKey || c.name || c.id || ''
        // Convert camelCase nameKey to readable: 'highBloodPressure' → 'High Blood Pressure'
        const readable = name.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()
        return `${readable} (${relatives.join(', ')})`
      })
      if (names.length > 0) return names.slice(0, 2).join('; ') + (totalCount > 2 ? ` +${totalCount - 2} more` : '')
      return `${totalCount} condition${totalCount === 1 ? '' : 's'} documented`
    }
    case 'social-lifestyle': {
      const sl = profile?.socialLifestyle
      if (!sl) return 'Lifestyle info recorded'
      const parts = []
      if (sl.tobacco === 'never' || sl.smoking === 'No') parts.push('Non-smoker')
      else if (sl.tobacco === 'current' || sl.smoking === 'Yes') parts.push('Smoker')
      if (sl.exercise || sl.exerciseFrequency) parts.push(sl.exercise || sl.exerciseFrequency)
      if (sl.occupation) parts.push(sl.occupation)
      if (sl.alcohol === 'none' || sl.alcohol === 'No') parts.push('No alcohol')
      return parts.length > 0 ? parts.slice(0, 3).join(' \u00B7 ') : 'Lifestyle info recorded'
    }
    default: return null
  }
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1.5 h-5">
      <span className="w-2 h-2 rounded-full animate-[dotPulseProfile_1s_ease-in-out_infinite]" style={{ background: '#A7D9FC' }} />
      <span className="w-2 h-2 rounded-full animate-[dotPulseProfile_1s_ease-in-out_0.2s_infinite]" style={{ background: '#A7D9FC' }} />
      <span className="w-2 h-2 rounded-full animate-[dotPulseProfile_1s_ease-in-out_0.4s_infinite]" style={{ background: '#A7D9FC' }} />
    </span>
  )
}

export default function ProfileScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    if (location.state?.openMenu) { setMenuOpen(true); window.history.replaceState({}, '') }
  }, [location.state])
  const [searchParams] = useSearchParams()
  const profile = useProfileStore((state) => state.profile)
  const syncSection = useProfileStore((state) => state.syncSection)
  const setProfilePhoto = useProfileStore((state) => state.setProfilePhoto)
  const loadProfile = useProfileStore((state) => state.loadProfile)
  const profileLoading = useProfileStore((state) => state.loading)
  const user = useUserStore((state) => state.user)
  const refreshUser = useUserStore((state) => state.refreshUser)
  const { setCurrentCase, cases } = useCaseStore()
  const { openAgent } = useAgent()
  const [menuOpen, setMenuOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importSuccess, setImportSuccess] = useState(null)
  const [agentOpen, setAgentOpen] = useState(false)
  const photoInputRef = useRef(null)
  const refreshedRef = useRef(false)
  const { t, currentLanguage, language } = useI18n()

  // Translated section definitions
  const SECTION_DEFS = useMemo(() => SECTION_DEFS_STRUCTURE.map(s => ({
    ...s,
    label: t('profileMain.' + s.labelKey),
    subtitle: t('profileMain.' + s.subtitleKey),
  })), [t])
  const [showChat, setShowChat] = useState(false)
  const [showChatText, setShowChatText] = useState(false)

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => setProfilePhoto(reader.result)
      reader.readAsDataURL(file)
    }
  }
  
  useEffect(() => {
    if (user && !user.orellaMemberId && !refreshedRef.current) {
      refreshedRef.current = true
      refreshUser()
    }
  }, [user, refreshUser])
  
  // Return-to-share flow
  const urlReturnToShare = searchParams.get('returnToShare') === 'true'
  const urlCaseId = searchParams.get('caseId')
  const urlMdOnboard = searchParams.get('mdOnboard') === 'true'
  const returnToShare = urlReturnToShare || location.state?.returnToShare || sessionStorage.getItem('returnToShare') === 'true'

  // v190.2 BT-36: Deep-link to specific profile section via ?section=insurance etc.
  useEffect(() => {
    const targetSection = searchParams.get('section')
    if (targetSection) {
      const match = SECTION_DEFS_STRUCTURE.find(s => s.id === targetSection)
      if (match) {
        navigate(match.path, { replace: true })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const returnCaseId = urlCaseId || location.state?.caseId || sessionStorage.getItem('returnCaseId')
  const returnMdOnboard = urlMdOnboard || location.state?.mdOnboard || sessionStorage.getItem('returnMdOnboard') === 'true'
  
  useEffect(() => {
    if (returnToShare && returnCaseId) {
      sessionStorage.setItem('returnToShare', 'true')
      sessionStorage.setItem('returnCaseId', returnCaseId)
      if (returnMdOnboard) sessionStorage.setItem('returnMdOnboard', 'true')
    }
  }, [returnToShare, returnCaseId, returnMdOnboard])
  
  // v213.7 (C-A-OB-FIX): Prefer profile.identification first, falling back
  // to users table. SSO sign-ups previously stored 'New User' literally
  // when the provider didn't return a name; reading identification first
  // ensures the avatar header shows the patient's real name once they
  // complete onboarding (which writes identification.firstName/lastName).
  const firstName = profile?.identification?.firstName || user?.firstName || profile?.firstName || ''
  const lastName  = profile?.identification?.lastName  || user?.lastName  || profile?.lastName  || ''
  const fullName = `${firstName} ${lastName}`.trim() || 'User'

  // v128.2: Restored from v116 — demographic helpers for Profile Hero Card
  const formatDOB = () => profile?.identification?.dob || null
  // PROD-1367: the age comes from dateUtils.calculateAge, which reads the date-only DOB as a local
  // calendar day. `new Date('1990-10-31')` is UTC midnight, the evening before in every US zone, so
  // the age went up one day early. A DOB it cannot read gives no age (not 'Unknown yrs').
  const calculateAge = () => {
    if (!profile?.identification?.dob) return null
    const age = ageFromDob(profile.identification.dob)
    return typeof age === 'number' ? age : null
  }
  const getSex = () => profile?.identification?.sex || null
  const getInfoLine = () => {
    const parts = []
    const dob = formatDOB()
    const age = calculateAge()
    const sex = getSex()
    if (dob) parts.push(dob)
    if (age !== null) parts.push(`${age} yrs`)
    if (sex) parts.push(formatSex(sex))
    return parts.length > 0 ? parts.join(' • ') : 'Complete your profile'
  }

  // UX-1 / TF-02 / TF-03: delegated. The local copy required only firstName +
  // lastName + dob for identification, so this screen reported the section
  // complete while ProfileIdentification refused to save it without an address
  // (TF-05). One definition now, in profileCompletion.js.
  const isComplete = (id) => isSectionComplete(id, profile)
  
  const completedIds = SECTION_DEFS.filter(s => isComplete(s.id)).map(s => s.id)
  const completedCount = completedIds.length
  const requiredSections = SECTION_DEFS.filter(s => s.required)
  const optionalSections = SECTION_DEFS.filter(s => !s.required)
  const requiredComplete = requiredSections.filter(s => isComplete(s.id)).length
  const optionalComplete = optionalSections.filter(s => isComplete(s.id)).length
  // v242.5.7 (#15): main % counts required sections only; optional tracked separately.
  // v242.61.0 (UX-1): computed by profileCompletion.js so the Done screen and this
  // screen cannot disagree — previously two independent copies of the arithmetic.
  const completionPercent = requiredCompletionPercent(profile)
  const optionalPercent = optionalCompletionPercent(profile)
  const allRequiredComplete = allRequiredCompleteFn(profile)

  // v151.7.1: Celebration popup when all required sections complete during share return flow
  const [showCompletionPopup, setShowCompletionPopup] = useState(false)
  const prevAllComplete = useRef(false)
  useEffect(() => {
    if (returnToShare && allRequiredComplete && !prevAllComplete.current) {
      setTimeout(() => setShowCompletionPopup(true), 400)
    }
    prevAllComplete.current = allRequiredComplete
  }, [allRequiredComplete, returnToShare])
  
  // PROD-1196: no section is ever locked; every card opens at any time and in any order.
  // 'active' is only the blue "next" suggestion (the first incomplete required section, and
  // the optional ones once every required section is done); 'open' is any other card to fill.
  const getSectionStatus = (section) => {
    if (isComplete(section.id)) return 'complete'
    if (section.required) {
      const firstIncomplete = SECTION_DEFS.filter(s => s.required).find(s => !isComplete(s.id))
      return firstIncomplete?.id === section.id ? 'active' : 'open'
    }
    return allRequiredComplete ? 'active' : 'open'
  }
  
  useEffect(() => {
    const hasPlayed = sessionStorage.getItem('profile_hub_animated')
    if (hasPlayed) { setShowChat(true); setShowChatText(true); return }
    const t1 = setTimeout(() => setShowChat(true), 300)
    const t2 = setTimeout(() => { setShowChatText(true); sessionStorage.setItem('profile_hub_animated', 'true') }, 1500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  // v148.0.0: Fetch fresh profile from API on mount (same pattern as MyCases/MyVisits)
  useEffect(() => {
    loadProfile()
  }, [])

  // v159.1: Show loading spinner while profile is loading
  if (profileLoading) {
    return (
      <div className="min-h-full bg-white flex flex-col">
        <OrellaLoader message={t('profileMain.loadingProfile')} />
      </div>
    )
  }

  const handleReturnToShare = () => {
    const caseIdToReturn = returnCaseId
    const mdOnboardToReturn = returnMdOnboard
    sessionStorage.removeItem('returnToShare')
    sessionStorage.removeItem('returnCaseId')
    sessionStorage.removeItem('returnMdOnboard')
    if (caseIdToReturn && cases.length > 0) {
      const targetCase = cases.find(c => c.id === caseIdToReturn)
      if (targetCase) { setCurrentCase(targetCase); navigate('/case/share', { replace: true, state: { caseId: caseIdToReturn, mdOnboard: mdOnboardToReturn } }); return }
    }
    if (caseIdToReturn) { navigate('/case/share', { replace: true, state: { caseId: caseIdToReturn, mdOnboard: mdOnboardToReturn } }); return }
    navigate(cases.length > 0 ? '/cases' : '/home', { replace: true })
  }

  const handleMedicalDataExtracted = async (data) => {
    let sectionsUpdated = []
    // ★ what the extraction OFFERED, so "nothing could be stored" is measurable below.
    const extracted = (data?.allergies?.length || 0) + (data?.medications?.length || 0)
      + (data?.medicalHistory?.length || 0) + (data?.familyHistory?.length || 0)
      + (data?.socialHistory ? 1 : 0)
    try {
      if (data.allergies?.length > 0) {
        const list = data.allergies.map(a => ({ name: a.substance, reaction: a.reaction || '' }))
        await syncSection('allergies', { hasAllergies: true, noKnownAllergies: false, list: [...(profile?.allergies?.list || []), ...list], completed: true })
        sectionsUpdated.push('Allergies')
      }
      if (data.medications?.length > 0) {
        const list = data.medications.map(m => ({ name: m.name, dosage: m.dosage || '', frequency: m.frequency || '' }))
        await syncSection('medications', { takesMedications: true, noMedications: false, list: [...(profile?.medications?.list || []), ...list], completed: true })
        sectionsUpdated.push('Medications')
      }
      // ★★★ TEST 1.03 — THE WRITER. This wrote `{ ...profile?.medicalHistory?.conditions }`,
      //   and `{ ...anArray }` is an OBJECT keyed by the indices, onto which the loop then
      //   added NAME keys with the value `true`. Truthy and not an array: the shape that
      //   white-screened the whole app from GlobalAgentProvider's useMemo, and that every
      //   surviving reader discarded in silence. A read-side guard alone would have turned
      //   the crash into exactly that silence, so the WRITE is repaired here and the readers
      //   coerce only so that already-poisoned profiles recover.
      if (data.medicalHistory?.length > 0) {
        const conditions = mergeImportedConditions(profile?.medicalHistory?.conditions, data.medicalHistory)
        const added = conditions.length - toConditionList(profile?.medicalHistory?.conditions).length
        await syncSection('medicalHistory', { conditions, completed: true })
        // ★ REPORTED ONLY IF SOMETHING WAS ACTUALLY STORED. The old handler pushed this
        //   unconditionally and rendered "Successfully imported: Medical History" over a
        //   value the app could not read.
        if (added > 0) sectionsUpdated.push('Medical History')
      }
      if (data.familyHistory?.length > 0) {
        const before = toConditionList(profile?.familyHistory?.conditions).length
          + toConditionList(profile?.familyHistory?.otherConditions).length
        const merged = mergeImportedFamilyConditions(
          profile?.familyHistory?.conditions, profile?.familyHistory?.otherConditions, data.familyHistory)
        await syncSection('familyHistory', { ...merged, completed: true })
        if (merged.conditions.length + merged.otherConditions.length > before) sectionsUpdated.push('Family History')
      }
      if (data.socialHistory) {
        await syncSection('socialLifestyle', { ...profile?.socialLifestyle, tobacco: data.socialHistory.tobacco, alcohol: data.socialHistory.alcohol, occupation: data.socialHistory.occupation, completed: true })
        sectionsUpdated.push('Social & Lifestyle')
      }
      setImportModalOpen(false)
      if (sectionsUpdated.length > 0) { setImportSuccess(t('profileMain.successfullyImported', { sections: sectionsUpdated.join(', ') })); setTimeout(() => setImportSuccess(null), 5000) }
      // ★ TEST 1.03 — the extraction offered something and NOTHING was stored. Saying nothing
      //   at all is what "reports success while dropping the data" degrades into once the
      //   success line is made conditional, so the patient is told instead of guessing.
      else if (extracted > 0) alert(t('errors2.failedToSaveSome'))
    } catch (err) { console.error('Failed to save extracted data:', err); alert(t('errors2.failedToSaveSome')) }
  }

  const goToSection = (path) => navigate(`${path}?guided=true`)

  return (
    <div className="min-h-full bg-white flex flex-col relative">
      <style>{`
        @keyframes dotPulseProfile { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }
        @keyframes fadeInProfile { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUpProfile { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Header — shared navy pattern (v242.17.0), carries to the top via NAVY_HEADER_ROUTES */}
      <AppHeader
        icon={User}
        title={t('profile.myProfile') || 'My Health Profile'}
        onMenu={() => setMenuOpen(true)}
        onLanguage={() => setLanguageOpen(true)}
      />
      
      <div className="flex-1 pb-8 space-y-4">
        {/* Return to Share — subtle inline chip when complete (popup handles the main CTA) */}
        {returnToShare && allRequiredComplete && !showCompletionPopup && (
          <button 
            onClick={handleReturnToShare}
            className="mx-4 w-auto flex items-center gap-2.5 p-3 rounded-xl bg-green-50 border border-green-200 active:scale-[0.98] transition-all"
          >
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle size={16} className="text-green-600" />
            </div>
            <span className="text-[13px] font-semibold text-green-700 flex-1 text-left">{t('profileMain.profileCompleteTapToContinue')}</span>
            <ChevronRight size={16} className="text-green-400" />
          </button>
        )}
        {returnToShare && !allRequiredComplete && (
          <div className="mx-4 flex items-center gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={14} className="text-amber-600" />
            </div>
            <p className="text-[12px] font-medium text-amber-700 flex-1">{t('profileMain.completeTheRequiredSectionsTo')}</p>
          </div>
        )}

        {/* Orella Dialogue — adaptive profile message (full-width to match Visits) */}
        <OrellaPageHeader
          message={getAdaptiveMessage(completedIds, allRequiredComplete, t)}
          sessionKey={`profile_${completedIds.length}`}
          chips={[
            ...(allRequiredComplete && !returnToShare ? [{ label: t('profileMain.startANewCase'), onClick: () => navigate('/home'), variant: 'primary' }] : []),
            { label: t('profileMain.askOrella'), onClick: () => openAgent() },
          ]}
          onAnimationDone={() => setShowChatText(true)}
        />

        {/* Profile content — gated behind chat bubble animation */}
        <div className="px-4" style={{
          opacity: showChatText ? 1 : 0,
          transform: showChatText ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
        }}>

        {/* Profile Hero Card — restored from v116 */}
        <div className="bg-white rounded-[14px] border border-gray-200 overflow-hidden">
          {/* Navy → Blue gradient accent bar */}
          <div className="h-[3px]" style={{ background: 'linear-gradient(to right, #15354F, #46A8F9)' }} />
          
          <div className="p-4">
            <div className="flex items-start gap-3.5">
              {/* Avatar — Clickable for photo upload */}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              <button
                onClick={() => photoInputRef.current?.click()}
                className="relative flex-shrink-0 group"
              >
                <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E9F8FF, #A7D9FC)' }}>
                  {profile?.profilePhoto ? (
                    <img src={profile.profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xl font-bold" style={{ color: '#15354F' }}>
                      {firstName.charAt(0)}{lastName.charAt(0)}
                    </span>
                  )}
                </div>
                {/* Camera overlay on hover/tap */}
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera size={18} className="text-white" />
                </div>
                {/* Status indicator */}
                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center" style={{ background: allRequiredComplete ? '#E9F8FF' : '#FEF3C7' }}>
                  {allRequiredComplete ? (
                    <Check className="w-3 h-3" style={{ color: '#46A8F9' }} />
                  ) : (
                    <Circle className="w-3 h-3 text-amber-600" />
                  )}
                </div>
              </button>
              
              {/* Patient Info */}
              <div className="flex-1 min-w-0">
                <h2 className="text-[18px] font-extrabold truncate" style={{ color: '#15354F' }}>
                  {fullName}
                </h2>
                <p className="text-[12px] text-gray-500 mt-0.5">
                  {getInfoLine()}
                </p>
                {user?.orellaMemberId && (
                  <p className="text-[11px] font-semibold mt-1" style={{ color: '#46A8F9' }}>
                    {user.orellaMemberId}
                  </p>
                )}
              </div>
            </div>
            
            {/* Completion Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12px] font-semibold" style={{ color: '#15354F' }}>
                  {allRequiredComplete ? t('profileMain.allRequiredComplete', { count: requiredSections.length }) : t('profileMain.xOfRequiredComplete', { defaultValue: '{{completed}} of {{total}} required complete', completed: requiredComplete, total: requiredSections.length })}
                </span>
                <span className={`text-[12px] font-bold`} style={{ color: completionPercent === 100 ? '#16A34A' : '#46A8F9' }}>
                  {completionPercent}%
                </span>
              </div>
              <div className="h-[5px] bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ width: `${completionPercent}%`, background: completionPercent === 100 ? '#66D170' : 'linear-gradient(to right, #A7D9FC, #46A8F9)' }}
                />
              </div>
              {/* Optional sections: separate quieter meter (#15) */}
              <div className="flex items-center justify-between mt-3 mb-1">
                <span className="text-[10.5px] font-semibold" style={{ color: '#94A3B8', letterSpacing: '0.02em' }}>{t('profileMain.optionalSections', 'Optional sections')}</span>
                <span className="text-[11px] font-bold" style={{ color: '#94A3B8' }}>{optionalPercent}%</span>
              </div>
              <div className="h-[3px] bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${optionalPercent}%`, background: optionalPercent === 100 ? '#9FD9B7' : '#B9C6D3' }}
                />
              </div>
              {/* Auto-fill link + last updated */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <button onClick={() => setImportModalOpen(true)} className="flex items-center gap-1.5 text-[12px] font-semibold active:opacity-70 transition-opacity" style={{ color: '#46A8F9', background: 'none', border: 'none' }}>
                  <Sparkles size={14} style={{ color: '#46A8F9' }} />
                  {t('profileMain.autoFill')}
                </button>
                {profile?.lastUpdated && (
                  <span className="text-[10px] text-gray-300">
                    {new Date(profile.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Import success toast */}
        {importSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
            <Check className="text-green-600 flex-shrink-0" size={20} /><p className="text-green-800 text-sm flex-1">{importSuccess}</p>
            <button onClick={() => setImportSuccess(null)} className="text-green-600"><X size={16} /></button>
          </div>
        )}

        {/* Section cards */}
        {/* Section cards — staggered reveal */}
        <div className="mt-2" style={{
          opacity: showChatText ? 1 : 0,
          transform: showChatText ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.4s ease 0.15s, transform 0.4s ease 0.15s',
        }}>
          <div className="space-y-2">
            {SECTION_DEFS.filter(s => s.required).map((section) => {
              const status = getSectionStatus(section)
              const IconComp = section.icon
              const summary = status === 'complete' ? getSectionSummary(section.id, profile) : null
              return (
                <button key={section.id} onClick={() => goToSection(section.path)}
                  className="w-full text-left rounded-2xl border-[1.5px] overflow-hidden transition-all active:scale-[0.98]"
                  style={{ borderColor: status === 'active' ? '#46A8F9' : '#E5E7EB', background: 'white' }}>
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: status === 'complete' ? '#E9F8FF' : status === 'active' ? '#E9F8FF' : '#F9FAFB' }}>
                      {status === 'complete' ? <Check size={20} style={{ color: '#46A8F9' }} strokeWidth={2.5} /> : <IconComp size={20} style={{ color: status === 'active' ? '#46A8F9' : '#9CA3AF' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-bold" style={{ color: '#15354F' }}>{section.label}</p>
                        {!isComplete(section.id) && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#D97706' }}>{t('profileMain.required')}</span>}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5 truncate">{summary || section.subtitle}</p>
                    </div>
                    {status === 'complete' && <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold" style={{ color: '#46A8F9' }}>{t('profileMain.edit')}</span><svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#66D170"/><path d="M6 10.5L8.5 13L14 7.5" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg></div>}
                    {status === 'active' && <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#46A8F9"/><path d="M8 6L14 10L8 14" fill="white"/></svg>}
                    {status === 'open' && <ChevronRight size={16} className="text-gray-300" />}
                  </div>
                </button>
              )
            })}
            
            <div className="pt-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300 mb-2 px-1">{t('profileMain.optionalEnhancesYourCare')}</p>
              <div className="space-y-2">
                {SECTION_DEFS.filter(s => !s.required).map((section) => {
                  const status = getSectionStatus(section)
                  const IconComp = section.icon
                  return (
                    <button key={section.id} onClick={() => goToSection(section.path)}
                      className="w-full text-left rounded-2xl border-[1.5px] overflow-hidden transition-all active:scale-[0.98]"
                      style={{ borderColor: '#E5E7EB', background: 'white' }}>
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: status === 'complete' ? '#E9F8FF' : '#F9FAFB' }}>
                          {status === 'complete' ? <Check size={20} style={{ color: '#46A8F9' }} strokeWidth={2.5} /> : <IconComp size={20} style={{ color: status === 'active' ? '#46A8F9' : '#9CA3AF' }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-bold" style={{ color: '#15354F' }}>{section.label}</p>
                          {status !== 'complete' && <p className="text-[11px] text-gray-400 mt-0.5 truncate">{section.subtitle}</p>}
                        </div>
                        {status === 'complete' && <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold" style={{ color: '#46A8F9' }}>{t('profileMain.edit')}</span><svg width="18" height="18" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#66D170"/><path d="M6 10.5L8.5 13L14 7.5" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg></div>}
                        {status !== 'complete' && <ChevronRight size={16} className="text-gray-300" />}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Hamburger Menu */}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
      
      {/* Language Selector (for header button) */}
      <LanguageSelector isOpen={languageOpen} onClose={() => setLanguageOpen(false)} />
      {importModalOpen && <MedicalDocumentUpload onDataExtracted={handleMedicalDataExtracted} onClose={() => setImportModalOpen(false)} />}
      <OrellaChatAgent isOpen={agentOpen} onClose={() => setAgentOpen(false)} caseId="profile" onNavigate={navigate} caseContext={{ phase: 0, status: 'profile', language, profileSummary: buildProfileSummary(profile) }} />

      {/* v151.7.0: Profile Complete Celebration Popup — pops center-screen so patient can't miss it */}
      {showCompletionPopup && returnToShare && allRequiredComplete && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6" onClick={() => setShowCompletionPopup(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div 
            className="relative bg-white rounded-3xl max-w-sm w-full overflow-hidden animate-[popIn_0.35s_ease-out]"
            onClick={e => e.stopPropagation()}
          >
            <style>{`@keyframes popIn { from { transform: scale(0.85) translateY(20px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }`}</style>
            {/* Green header band */}
            <div className="pt-8 pb-5 text-center" style={{ background: 'linear-gradient(135deg, #66D170 0%, #22C55E 100%)' }}>
              <div className="w-16 h-16 rounded-full bg-white/20 mx-auto mb-3 flex items-center justify-center">
                <CheckCircle size={36} className="text-white" />
              </div>
              <h2 className="text-xl font-extrabold text-white">{t('profileMain.profileComplete')}</h2>
              <p className="text-white/80 text-[13px] mt-1 px-6">{t('profileMain.everythingYourDoctorNeedsIs')}</p>
            </div>

            {/* Body */}
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-2.5">
                {[
                  { label: t('profileMain.identification'), icon: '✓' },
                  { label: t('profileMain.insurance'), icon: '✓' },
                  { label: t('profileMain.allergies'), icon: '✓' },
                  { label: t('profileMain.medications'), icon: '✓' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 px-2 py-1 rounded-full">
                    <Check size={10} strokeWidth={3} className="text-green-500" />
                    {s.label}
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => { setShowCompletionPopup(false); handleReturnToShare() }}
                className="w-full py-3.5 rounded-2xl text-white font-bold text-[15px] flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
                style={{ backgroundColor: '#15354F', boxShadow: '0 8px 20px rgba(21,53,79,0.25)' }}
              >
                <Share2 size={16} /> {t('profileMain.continueToShare')}
              </button>
              
              <button
                onClick={() => setShowCompletionPopup(false)}
                className="w-full py-2 text-[12px] font-medium text-gray-400"
              >
                {t('profileMain.stayOnProfile')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
